import { useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Shield,
  FileText,
  Receipt,
  Calculator,
  Wrench,
  HelpCircle,
  LogOut,
  User,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

const portalCards = [
  {
    icon: Shield,
    title: "PMOC",
    description: "Laudos, relatórios, formulários e ARTs",
    gradient: "from-indigo-500 to-purple-500",
    link: "/portal/pmoc",
    enabled: true,
    permission: "pmoc",
  },
  {
    icon: Receipt,
    title: "Notas Fiscais",
    description: "Notas fiscais emitidas",
    gradient: "from-purple-500 to-pink-500",
    link: "/portal/notas-fiscais",
    enabled: true,
    permission: "nf",
  },
  {
    icon: Calculator,
    title: "Propostas",
    description: "Propostas comerciais enviadas",
    gradient: "from-blue-500 to-indigo-500",
    link: "/portal/propostas",
    enabled: true,
    permission: "proposta",
  },
  {
    icon: Wrench,
    title: "Manutenções",
    description: "Histórico e agendamento de manutenções",
    gradient: "from-cyan-500 to-blue-500",
    link: "/portal/manutencoes",
    enabled: false,
    permission: null,
  },
  {
    icon: HelpCircle,
    title: "Suporte",
    description: "Abra chamados e acompanhe atendimentos",
    gradient: "from-teal-500 to-cyan-500",
    link: "/portal/suporte",
    enabled: false,
    permission: null,
  },
];

export function Portal() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = "/area-cliente";
    } else if (isAdmin) {
      // Admin não deve acessar o portal do cliente
      window.location.href = "/dashboard";
    }
  }, [isAuthenticated, isAdmin]);

  const handleLogout = () => {
    logout();
    window.location.href = "/area-cliente";
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  // Permissões do usuário (se não definido, tem acesso total)
  const userPermissions = user.permissions || ["pmoc", "nf", "proposta"];
  
  // Filtrar cards baseado nas permissões
  const visibleCards = portalCards.filter(card => {
    // Cards sem permissão definida (em breve) sempre aparecem
    if (!card.permission) return true;
    // Cards com permissão só aparecem se o usuário tem acesso
    return userPermissions.includes(card.permission);
  });

  return (
    <>
      <Helmet>
        <title>Portal do Cliente | Arcgel</title>
        <meta name="description" content="Portal do cliente Arcgel - Acesse seus documentos" />
      </Helmet>

      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header do Portal */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8"
          >
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Olá, <span className="text-cyan-400">{user.name.split(' ')[0]}</span>
              </h1>
              <p className="text-slate-400">Bem-vindo ao seu portal de documentos</p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50">
                <User className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-slate-300">{user.email}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sair</span>
              </button>
            </div>
          </motion.div>

          {/* Cards do Portal */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {card.enabled ? (
                  <Link
                    to={card.link}
                    className="block p-6 rounded-xl bg-slate-800/30 border border-slate-700/50 hover:border-cyan-500/30 hover:bg-slate-800/50 transition-all duration-300 group"
                  >
                    <div
                      className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${card.gradient} mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
                    <p className="text-slate-400 text-sm">{card.description}</p>
                  </Link>
                ) : (
                  <div className="block p-6 rounded-xl bg-slate-800/20 border border-slate-700/30 opacity-60 cursor-not-allowed relative">
                    <span className="absolute top-3 right-3 px-2 py-1 text-xs font-medium bg-slate-700/50 text-slate-400 rounded-full">
                      Em breve
                    </span>
                    <div
                      className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${card.gradient} mb-4 opacity-50`}
                    >
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
                    <p className="text-slate-400 text-sm">{card.description}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Área de Avisos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 p-6 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20"
          >
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">Novos documentos disponíveis</h3>
                <p className="text-sm text-slate-400">
                  Confira os últimos documentos adicionados ao seu portal clicando nas categorias acima.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
